// Animal join SFX when players enter or change color in animal-style rooms

import { useEffect, useCallback, useRef } from 'react';
import { getAnimalSoundEffectIdForPlayerColorKey } from '@airhead-games/shared/configs';
import type { RoomPlayer } from '@airhead-games/shared/types';
import { useMedia } from './useMedia';

interface UseAnimalJoinSoundsArgs {
  players: RoomPlayer[];
  isAnimalStyleRoom: boolean;
  isReplayingEvents: boolean;
}

export function useAnimalJoinSounds({
  players,
  isAnimalStyleRoom,
  isReplayingEvents,
}: UseAnimalJoinSoundsArgs): void {
  const { playSoundEffect } = useMedia();
  const previousAnimalPlayerColorsRef = useRef<Record<string, string>>({});
  const hasSeededAnimalPlayerSoundsRef = useRef(false);
  const pendingAnimalJoinSoundTimeoutsRef = useRef<Record<string, number>>({});

  const clearPendingAnimalJoinSound = useCallback((playerId: string) => {
    const timeoutId = pendingAnimalJoinSoundTimeoutsRef.current[playerId];
    if (timeoutId === undefined) {
      return;
    }

    window.clearTimeout(timeoutId);
    delete pendingAnimalJoinSoundTimeoutsRef.current[playerId];
  }, []);

  const clearAllPendingAnimalJoinSounds = useCallback(() => {
    for (const timeoutId of Object.values(
      pendingAnimalJoinSoundTimeoutsRef.current
    )) {
      window.clearTimeout(timeoutId);
    }

    pendingAnimalJoinSoundTimeoutsRef.current = {};
  }, []);

  useEffect(() => {
    return () => {
      clearAllPendingAnimalJoinSounds();
    };
  }, [clearAllPendingAnimalJoinSounds]);

  useEffect(() => {
    const nextPlayerColors = Object.fromEntries(
      players.map((player) => [player.playerId, player.playerColor])
    );

    for (const playerId of Object.keys(
      pendingAnimalJoinSoundTimeoutsRef.current
    )) {
      if (!(playerId in nextPlayerColors)) {
        clearPendingAnimalJoinSound(playerId);
      }
    }

    if (!isAnimalStyleRoom) {
      clearAllPendingAnimalJoinSounds();
      previousAnimalPlayerColorsRef.current = nextPlayerColors;
      hasSeededAnimalPlayerSoundsRef.current = false;
      return;
    }

    if (!hasSeededAnimalPlayerSoundsRef.current) {
      clearAllPendingAnimalJoinSounds();
      previousAnimalPlayerColorsRef.current = nextPlayerColors;
      hasSeededAnimalPlayerSoundsRef.current = true;
      return;
    }

    const previousPlayerColors = previousAnimalPlayerColorsRef.current;
    previousAnimalPlayerColorsRef.current = nextPlayerColors;

    if (isReplayingEvents) {
      clearAllPendingAnimalJoinSounds();
      return;
    }

    for (const player of players) {
      const previousColor = previousPlayerColors[player.playerId];
      const isNewPlayer = previousColor === undefined;
      const colorChanged = previousColor !== player.playerColor;

      if (isNewPlayer) {
        clearPendingAnimalJoinSound(player.playerId);

        pendingAnimalJoinSoundTimeoutsRef.current[player.playerId] =
          window.setTimeout(() => {
            delete pendingAnimalJoinSoundTimeoutsRef.current[player.playerId];

            const latestColor =
              previousAnimalPlayerColorsRef.current[player.playerId];
            if (!latestColor) {
              return;
            }

            const soundEffectId =
              getAnimalSoundEffectIdForPlayerColorKey(latestColor);
            if (!soundEffectId) {
              return;
            }

            void playSoundEffect(soundEffectId);
          }, 2000);
        continue;
      }

      if (!colorChanged) {
        continue;
      }

      clearPendingAnimalJoinSound(player.playerId);

      const soundEffectId = getAnimalSoundEffectIdForPlayerColorKey(
        player.playerColor
      );
      if (!soundEffectId) {
        continue;
      }

      void playSoundEffect(soundEffectId);
    }
  }, [
    clearAllPendingAnimalJoinSounds,
    clearPendingAnimalJoinSound,
    isAnimalStyleRoom,
    isReplayingEvents,
    players,
    playSoundEffect,
  ]);
}
